import {Logger, PubSub, Viewport} from '@mamdev/cad.static-next-lib';
import {Config} from '../../ts/services/config';
import DarkMode from '../../ts/services/darkmode';
import UrlPath from '../../ts/services/url-path';
import PageType from '../../ts/services/pagetype';

import {handleAdExpandingSky} from './ads/adexpandingsky';
import {handleInterstitial} from './ads/adinterstitial';
import {handleSitebarOverlay, adjustSitebarIframe} from './ads/adsitebar';
import {handleStickyBottomAd} from './ads/adstickybottom';
import {setBackground} from './ads/adbackground';
import {AdStickyTopMew} from './ads/adstickytopmew';
import {handleAdResize, handleAddemandExpand, adjustIframe, getAdViewport, getLayoutClass, getMaxWidthParam, isAdContainerDisplayed} from './ads/adutils';

import type {IAdService, IAdServiceConfig, IAdData} from '../../definitions';

interface MessageData {
  data: {
    method: string;
    sky: boolean;
    top: boolean;
    rect: boolean;
    all: boolean;
  };
}

/** Communicates with the AdService */
class Ads {
  private readonly name = 'ads';
  private readonly adContainerSelector = '.co-container:not([data-processed="true"])';
  private readonly tagIdMapping: Record<string, string> = {
    topbanner: 'top',
    sky_on_overlay: 'right_layer'
  };

  private initialized: boolean;
  private adCounter: Record<string, number> = {}; // Keep track of how many ads per tagid have been inserted in the page
  private adService: IAdService;
  private adServiceConfig: IAdServiceConfig;
  private adReplacementSuffix = ''; // In case of fallback-ads, a suffix must be added to tagid
  private isInApp = false;
  private gotSitebar = false;
  private gotMobileInterstitial = false;
  private sitebarActive = false;
  private adSlotTop: Element | null;
  private adSlotRight: Element | null;

  initialize(useDefaultConfig: boolean) {
    if (this.initialized) return;

    this.adService = window.AdService;
    this.adServiceConfig = Config.get('AdServiceConfig') as IAdServiceConfig;
    this.isInApp = this.adServiceConfig.init.deviceclient === 'app';

    this.adSlotTop = document.querySelector('.wrapper .coc-top .co-topbanner');
    this.adSlotRight = document.querySelector('.main .coc-sky .co-sky');

    this.modifyAdMarkup();
    this.initVisibilityConfig();
    this.initAdService();
    this.initAdRefresh();

    // handling internal events
    PubSub.subscribe<Element[]>('dynamicAdsNeeded', adContainersData => {
      Logger.log(this.name, 'dynamicAdsNeeded');
      this.fillAdContainers(adContainersData);
    });

    if (useDefaultConfig) {
      this.adService.setParam('defaultconfig', 1);
    }

    this.initialized = true;
    Logger.log(this.name, 'init');
  }

  /**
   * Mofify ad DOM markup
   * replace dynamic slotID with a static definition
   */
  modifyAdMarkup(): void {
    // positions that need to be modified (selector: slotid)
    const positions: Record<string, string> = {
      'co-topbanner': 'top',
      'co-sky': 'right'
    };

    // search dom just once
    const adContainers = document.getElementsByClassName('co-container');

    // after the first box ad, all the others will have their tagid changed to box_x
    let firstBoxAdFound = false;

    // find relevant ad container and replace id with slotid
    for (const adContainer of adContainers) {
      if (adContainer instanceof HTMLElement) {
        for (const key in positions) {
          if (adContainer.classList.contains(key)) {
            adContainer.id = positions[key];
            break;
          }
        }

        if (adContainer.getAttribute('data-service-param-tagid') === 'box') {
          if (firstBoxAdFound) {
            adContainer.setAttribute('data-service-param-tagid', 'box_x');
          } else {
            firstBoxAdFound = true;
          }
        }
      }
    }
  }

  /**
   * AdService setting to load ads before they become visible on the page.
   * The AdService will start loading ads the amount of pixels specified
   * before their container would reach the visible viewport.
   *
   * In case this is not specified, the default value will be 300 pixels.
   */
  initVisibilityConfig() {
    const asConfig = this.adServiceConfig || {config: {configurations: {visibilityLookahead: 300}}};
    const configurations = asConfig.config.configurations || {visibilityLookahead: 300};
    const lookahead = configurations.visibilityLookahead ?? 300;

    this.adService.setConfig({'visibility-lookahead': lookahead});
  }

  /** Initialize the adService */
  initAdService() {
    this.adService.push(() => {
      const initParams = this.adServiceConfig.init;
      const offsetWidth = document.documentElement.offsetWidth;
      initParams.layoutclass = getLayoutClass(this.isInApp, offsetWidth);
      initParams.vw = getAdViewport(document.documentElement.clientWidth);

      // additional parameter to tell AdServer 'we are in AdService2 mode' > as = 1, confusing!
      initParams.as = 1;

      // check if breaking news module is present, to avoid overlapping sticky ads
      initParams.bn = document.querySelector('[data-mod-name="breakingnews-sticky"]') ? 1 : 0;

      this.adService.AdServer('adition-asp').setParam(initParams);
      this.adService.getDefaultAdServer().setParam(initParams);

      // Set dark mode if the user is using it.
      if (DarkMode.isActivated()) {
        Logger.log(this.name, 'darkmode activated');
        this.adService.setParam('darkmode', '1');
      } else {
        Logger.log(this.name, 'darkmode not activated');
      }

      // Special param for Taboola on Welcomeback layer CADNPCA-15147
      if (window.ui.wb?.taboola) {
        // this.adService.setParam('special', '50');
        this.adService.setSlotParam('rectangle_layer', 'special', '50');
      }

      const directInitParams = this.adServiceConfig.directInit;
      if (directInitParams) {
        for (const [k, v] of Object.entries(directInitParams)) {
          this.adService.setDirectParam(k, v.toString());
        }
      }

      const templateURL = UrlPath.moduleAssets + '/ad2/native-teaser.mst';

      this.adService.setTemplateUrl(templateURL);
      this.adService.setConfig('data-attribute-namespace', 'service');

      // called before a native teaser is being rendered
      this.adService.on('render.begin', (data: IAdData) => {
        const renderData = data.data.render;
        if (renderData) {
        // replace ##br## characters with a real <br> element to split lines
          renderData.headline = renderData.headline.replace(/##br##/g, '<br>');

          if (renderData.subline) {
            renderData.subline = renderData.subline.replace(/##br##/g, '<br>');
          }
        }
      });

      // define custom css for sitebar ad content(!)
      this.adService.setIframeCss({
        sitebar: {
          css: 'html, body, body > div {height: 100%;}'
        },
        contentAdResponsive: {
          css: 'html, body, body > div {height: 100%}'
            + 'body > div > * {margin: 0 auto}'
        }
      });

      const asUrl = this.adServiceConfig.adServiceConnectorUrl;
      this.adService.setConfig('adservice-iframe-script-url', asUrl);
      Logger.log(this.name, 'asUrl = ' + asUrl);
      Logger.log(this.name, 'iframe script url = ' + (this.adService.getConfig('adservice-iframe-script-url') as string));
      this.adService.setConfig('request-ad-only-if-visible', true);

      // CADNPCA-8556
      this.adService.setConfig('secure', true);
      const iframeUrl = this.adServiceConfig.nonFriendlyIFrameDocument;
      if (iframeUrl) {
        this.adService.setConfig('nonfriendlyiframe-document', iframeUrl);
      }

      this.adService.setConfig('visibility-precheck', slot => {
        const element = slot.getDomNode() as HTMLElement;

        if (!element) {
          Logger.log(this.name, 'visibility-precheck: slot.getDomNode did not return a valid DOM node!');
          Logger.log(this.name, (slot.id as string));
          return;
        }

        return isAdContainerDisplayed(element);
      });

      // whitelist the right and ambient to skip the visibility check
      this.adService.AdSlot('ambient').setConfig('request-ad-only-if-visible', false);
      this.adService.AdSlot('right').setConfig('request-ad-only-if-visible', false);

      // Change how Pre/Postroll are called - CADNPCA-7542
      this.adService.AdSlot('v_preroll').setAdServer('adition-asp');
      this.adService.AdSlot('v_postroll').setAdServer('adition-asp');

      // Set custom function to retrieve a specific ad slot for the AdService
      // Must return a DOM Element
      this.adService.setConfig('slot-getdomnode-function', function(slot) {
        if (slot.domNode) {
          return slot.domNode as Element;
        } else {
          return document.querySelector('.co-container[data-service-slot="' + slot.id + '"]');
        }
      });

      // Taboola variant test special param CADNPCA-16662
      const tblSpec = Config.get('taboolaSpecial');
      if (tblSpec) {
        this.adService.setParam('special', tblSpec as string);
      }
      // Taboola without iframe test CADNPCA-16662
      if (Config.get('taboolaNoIframe')) {
        this.adService.AdSlot('relatedbox').setConfig('secure', false);
      }
    });

    this.setAdServiceEvents(); // split into a new function to satisfy sonar
    this.handleRectangleSlot();
    this.setAdServiceRules();
    this.fillAdContainers(undefined);
    this.callAnalyticsTracking();

    PubSub.subscribe('mod:pingAdService', () => {
      PubSub.publish('ad:adServiceReady');
    });

    PubSub.publish('ad:adServiceReady');
  }

  /** Get devicesclass and ff from AdService and call analytics */
  callAnalyticsTracking() {
    const params = this.adService.getParams();

    PubSub.publish('ad:gotDeviceClass', {
      layoutclass: params.layoutclass,
      deviceclass: params.deviceclass,
      ff: params.ff
    });
  }

  /** Setup adservice events */
  setAdServiceEvents() {
    this.adService.push(() => {
      this.adService.on('adblock.indirect', () => {
        Logger.log(this.name, 'adblock.indirect');
        this.adService.setDefaultAdServer('adition-asp-united-uimserv');
        this.adService.setParam('deviceclient', 'browser_clean');
      });

      this.adService.on('adblock.direct', () => {
        Logger.log(this.name, 'adblock.direct');
        const defaultDeviceClient = (window.ui?.pageinfo?.deviceclient) ?? 'browser';
        this.adService.setDefaultAdServer('adition-asp-united-infos');
        this.adReplacementSuffix = '_united';
        this.adService.setParam('deviceclient', defaultDeviceClient);
      });

      this.adService.on('adblock.detectionfinished', () => {
        Logger.log(this.name, 'adblock.detectionfinished');
        const unitedBlocked = this.adService.info.abd().details.fyi.unitedBlocked;
        if (unitedBlocked) {
          this.adService.setDefaultAdServer('adition-asp-info');
          this.adReplacementSuffix = '_info';
        }
      });

      this.adService.on('ad.urlReady', (data: IAdData) => {
        const isVerticalVideoPage = document.body.classList.contains('vertical-video-page');

        if (isVerticalVideoPage) return;

        const section = this.adService.getParam('section'); // only used on article pages
        const layoutclass = this.adService.getParam('layoutclass'); // only for mew devices (b: big, m: mid, s: small)
        const categorytype = this.adService.getParam('categorytype'); // disabled for slideshow and video

        if (layoutclass === 's' && categorytype !== 'slideshow' && categorytype !== 'video' && data.slot.id === 'top' && section === 'content') {
          AdStickyTopMew();
        }
      });

      // listen if background should be colorized
      this.adService.on('ad.property.background', (data: IAdData) => {
        setBackground(data);
      });

      // if it is a sitebar
      this.adService.on('ad.size:220x600', (data: IAdData) => {
        if (data.slot.params.tagid === 'right_layer') {
          this.adService.useIframeCss('sitebar', data.slot.id);
          handleSitebarOverlay(data);
        } else {
          this.gotSitebar = true;
          document.documentElement.classList.add('has-ad-sitebar');
          this.adService.useIframeCss('sitebar', 'right');
          this.handleSitebar(data);
        }
      });

      // if it is a sitebar on topposition
      this.adService.on('ad.size:300x600', (data: IAdData) => {
        if (!this.gotSitebar) {
          this.gotSitebar = true;
          document.documentElement.classList.add('has-ad-sitebar');
          document.documentElement.classList.add('has-ad-widesky');
          this.adService.useIframeCss('sitebar', 'right');
          data.wideSky = true;
          this.handleSitebar(data);
        }
      });

      // floor ad desktop
      this.adService.on('ad.size:728x200', (data: IAdData) => {
        if (data.slot.params.tagid === 'ambient') {
          data.target?.classList.add('floor-ad-desktop');
        }
      });

      // reset the stored sizes if is a refresh triggered
      this.adService.on('ad.begin', (data: IAdData) => {
        if (data.data.isRefresh) {
          window.ui.triggeredAdSizes = {};
        }
      });

      // resize sitebar iframe on finished
      this.adService.on('ad.finished', (data: IAdData) => {
        this.handleAdFinished(data);
      });

      // listen to baseboard ad or floorad
      this.adService.on('ad.property.sticky-bottom', (data: IAdData) => {
        handleStickyBottomAd(data, this.adSlotTop);
      });

      // Adjust size of iframe in ad container
      // trigger from ads, such as floorAd or videoNativeTeaser
      this.adService.on('ad.property.resize', (data: IAdData) => {
        handleAdResize(data);
      });

      // Create responsive ad containers
      this.adService.on('ad.property.responsive', (data: IAdData) => {
        // extend object
        // static height has been defined by TAM / Mathias Butterer in CADNPCA-3900
        data.data.arguments = [{
          width: '100%',
          height: 280
        }];

        // resize ad iframe
        handleAdResize(data);
      });

      // listen to AdService.expandAd (for ads such as pushdown ad)
      this.adService.on('addemand.expand', (data: IAdData) => {
        handleAddemandExpand(data, window.innerHeight);
      });

      // mobile interstitial
      this.adService.on('addemand.openlayer', () => {
        if (!this.gotMobileInterstitial) {
          this.gotMobileInterstitial = true;
          handleInterstitial(this.adSlotTop, true);
        }
      });
      this.adService.on('addemand.closelayer', () => {
        if (this.gotMobileInterstitial) {
          this.gotMobileInterstitial = false;
          handleInterstitial(this.adSlotTop, false);
        }
      });

      // Expanding sky
      this.adService.on('ad.property.expanding', (data: IAdData) => {
        handleAdExpandingSky(data);
      });

      this.setEnableDisableListener();
    });
  }

  handleSitebar(data: IAdData): void {
    this.sitebarActive = true;

    data.target?.classList.add('ad-sitebar', 'fixed');
  }

  /** Toggles visibility of the rectangle ad slot (<div ... data-service-slot="rectangle">) */
  handleRectangleSlot() {
    try {
      // Case: No Ads Allowed
      const noAdsAllowed = !this.adServiceConfig.enabled;
      if (noAdsAllowed) {
        return;
      }

      const headerElement = document.getElementsByClassName('left-col')[0];
      if (!headerElement) {
        return;
      }

      // show rectangle
      const headerRectangle = headerElement.getElementsByClassName('co-rectangle')[0];

      if (headerRectangle) {
        headerRectangle.classList.remove('ad-disabled');
        document.documentElement.classList.remove('co-rectangle-disabled');
      }
    } catch (e) {
      Logger.logUnknownError(e, this.name);
    }
  }

  handleAdFinished(data: IAdData) {
    // resize iframe only if width or height is zero
    adjustIframe(data);

    const slotId = data.slot.id;
    const tagId = data.slot.params.tagid;

    if (tagId.includes('right_layer')) {
      this.adService.getDefaultAdServer().resetSlot('right_layer');
    }

    if (slotId === 'right') {
      PubSub.publish('layoutChanged');

      if (this.gotSitebar) {
        adjustSitebarIframe();
      }
    }

    if (slotId === 'top') {
      PubSub.publish('layoutChanged');

      if (this.gotMobileInterstitial) {
        const ifr = document.querySelector('.co-topbanner.interstitial > iframe');
        if (ifr instanceof HTMLIFrameElement) {
          ifr.removeAttribute('style');
          ifr.removeAttribute('height');
        }
      }
    }

    if (tagId === 'contentad' || tagId === 'contentad_x') {
      this.adService.useIframeCss('contentAdResponsive', slotId);
    }

    switch (tagId) {
      case 'box':
      case 'box_x':
      case 'contentad':
      case 'contentad_x':
      case 'lazyload':
      case 'lazyload_x':
        this.enableTeaserClickoutTracking(data.target);
    }
  }

  /** Throw exclusion rules at the adservice */
  setAdServiceRules() {
    this.adService.push(() => {
      this.adService.setExclusionRuleSet(this.adServiceConfig.config.exclusionRules);
      Logger.log(this.name, 'HB: exclusion rules set');
    });

    this.adService.push(() => {
      Logger.log(this.name, 'HB: setting auction config...');
      try {
        this.adService.setAuctionConfig(this.adServiceConfig.config.prebidRules);
        Logger.log(this.name, 'HB: auction config set');
      } catch (e) {
        Logger.log(this.name, 'HB: failed setting auction config!');
        Logger.logUnknownError(e, this.name);
      }
    });
  }

  /** Find (unfilled) ad-containers on the page and request ads for them */
  fillAdContainers(adList: Element[] | undefined) {
    Logger.log(this.name, 'fill ad containers');

    adList = adList ?? [];

    const emptyAdContainers = (adList.length > 0) ? adList : document.querySelectorAll(this.adContainerSelector);
    let isSlideshow = false;
    // disable content ads for desktop slideshow
    if (document.body.classList.contains('gallery-page') && Viewport.isDesktop()) {
      isSlideshow = true;
    }

    for (let i = 0, len = emptyAdContainers.length; i < len; i++) {
      const adContainer = emptyAdContainers[i] as HTMLElement;

      let tagID = adContainer.dataset.serviceParamTagid;
      let slotID = adContainer.dataset.serviceSlot;

      if (!tagID) {
        continue;
      }
      if (tagID === 'rectangle_slideshow' && isSlideshow) {
        continue;
      }

      tagID = (this.tagIdMapping[tagID] || tagID).replace(/(?:_info|_united)$/, '');

      if (Viewport.isDesktop() && tagID === 'top') {
        Logger.log(this.name, 'Avoid requesting top position on desktop');
        continue;
      }
      // More exceptions: viewport vs. ad position (CADNPCA-12119)
      if (!Viewport.largerThan('desktop') && (tagID === 'rectangle')) {
        if (!PageType.isLogoutPage && !PageType.isInboxNewsPage) {
          Logger.log(this.name, 'No "rectangle" for deskop- VP, except logout / inboxnews page');
          continue;
        }
      }
      if (!Viewport.largerThan('large') && (tagID === 'right')) {
        Logger.log(this.name, 'Avoid requesting right position on VP less than large');
        continue;
      }

      // Avoid requesting an ad for the same slot more than once
      adContainer.dataset.processed = 'true';

      // Filter out ending _x of some tagIDs
      const baseTagID = tagID.replace(/_x$/, '');

      tagID += this.adReplacementSuffix; // CADNPCA-6464

      if (!slotID) {
        // Keep track of how many ads per tagID have been requested and generate a slotID
        if (this.adCounter[baseTagID] === undefined) {
          this.adCounter[baseTagID] = 0;
          slotID = baseTagID;
        } else {
          this.adCounter[baseTagID] += 1;
          slotID = baseTagID + '_' + this.adCounter[baseTagID];
        }

        adContainer.setAttribute('data-service-slot', slotID);
        adContainer.setAttribute('data-service-param-tagid', tagID);

        Logger.log(this.name, 'Found empty container ' + slotID);
        Logger.log(this.name, 'adCounter[' + tagID + '] = ' + this.adCounter[tagID] + '; slotID = ' + slotID);
      } else {
        // Refresh ad slot
        Logger.log(this.name, 'Refreshing ad slot ' + slotID);
      }

      this.callAd(slotID, tagID, adContainer);
    }
  }

  /** Request an ad */
  callAd(containerId: string, adTagId: string, adContainer: HTMLElement) {
    Logger.log(this.name, 'To request "' + adTagId + '" to container ' + containerId + '.');
    const offsetWidth = document.documentElement.offsetWidth;
    const defaultMaxWidth = adContainer ? adContainer.offsetWidth : null;
    this.adService.AdSlot(containerId).setParam('maxwidth', getMaxWidthParam(adTagId, this.isInApp, offsetWidth, defaultMaxWidth).toString());
    // Taboola on welcome-back layer: CADNPCA-15147
    if (adContainer.hasAttribute('data-taboola-wb')) {
      console.log('Taboola on WB-layer');
      this.adService.AdSlot(containerId).setParam('special', '50');
    }
    this.adService.push(containerId);

    // In case when all slots are disabled
    // AdService doesn't trigger the main application to disable the container
    if (!this.adService.AdSlot(containerId).config.enabled) {
      this.disableAd(adContainer, adTagId);
    }
  }

  /** Refreshing contentads inside article image/slide is expanded (was previously used in img gallery) */
  handleContentAds(slideShowOffset: number, slideShowMode: string) {
    try {
      const allContentAds = document.querySelectorAll<HTMLElement>('.co-container[data-service-param-tagid="contentad"]');
      if (allContentAds) {
        allContentAds.forEach(element => {
          const slotId = element.getAttribute('data-service-slot');
          if (slotId) {
            if (slideShowMode === 'unexpanded') {
              this.adService.AdSlot(slotId).setParam('maxwidth', (element.offsetWidth - slideShowOffset).toString());
              this.adService.refresh(slotId);
              Logger.log(this.name, 'setMaxWidth ' + element.offsetWidth + ' - ' + slideShowOffset + ' -> ' + (element.offsetWidth - slideShowOffset));
              Logger.log(this.name, 'refreshing ' + slotId);
            } else {
              this.adService.AdSlot(slotId).setParam('maxwidth', (element.offsetWidth + slideShowOffset).toString());
              Logger.log(this.name, 'setMaxWidth ' + element.offsetWidth + ' + ' + slideShowOffset + ' -> ' + (element.offsetWidth + slideShowOffset));
            }
          }
        });
      }
    } catch (e) {
      Logger.logUnknownError(e, this.name);
    }
  }

  /** Ad refresh is triggered by different events and broadcast pipelines such as pubsub or postMessage */
  initAdRefresh() {
    // refresh AdPosition 'medium Rectangle' in left active tab
    PubSub.subscribe('ad:refreshAdMedRectInTab', () => {
      try {
        const leftCol = document.getElementsByClassName('left-col')[0];
        if (leftCol) {
          leftCol.querySelectorAll('.co-rectangle').forEach(element => {
            const slotId = element.getAttribute('data-service-param-tagid');
            this.adService.refresh(slotId ?? undefined);
          });
        }
      } catch (e) {
        Logger.logUnknownError(e, this.name);
      }
    });

    // refresh AdPosition 'TopBanner'
    PubSub.subscribe('ad:refreshAdTop', () => {
      this.resetSitebar();
      // remove stickiness CADNPCA-4354
      const stickyClass = 'ad-sticky-top';
      const stickyCon = document.getElementsByClassName(stickyClass)[0];
      if (stickyCon) {
        stickyCon.classList.remove(stickyClass);
        const mainCon = document.getElementsByClassName('main')[0] as HTMLElement;
        mainCon.style.paddingTop = '0';
      }
      this.adService.refresh('top');
    });

    // refresh AdPosition 'SkyRight'
    PubSub.subscribe('ad:refreshAdSky', () => {
      this.resetSitebar();
      this.adService.refresh('right');
    });

    // remove 'style'-Attribute (background-color)
    PubSub.subscribe('ad:removeBackgroundColor', () => {
      document.querySelector('.coc-top')?.removeAttribute('style');
      document.querySelector('.coc-sky')?.removeAttribute('style');
    });

    // whitelabel/iFrame postMessage listener
    window.addEventListener('message', (e: MessageData) => {
      if (e.data.method === 'adRefresh') {
        // trigger adRefresh if key is true
        if (e.data.sky) {
          PubSub.publish('ad:refreshAdSky');
        }
        if (e.data.top && Viewport.isMobile()) {
          PubSub.publish('ad:refreshAdTop');
        }
        if (e.data.rect) {
          PubSub.publish('ad:refreshAdMedRectInTab');
        }
        if (e.data.all) {
          this.adService.refresh();
        }
      }
    }, false);

    PubSub.subscribe('ad:refreshAll', () => {
      this.refreshAllAdSlots();
    });
  }

  refreshAllAdSlots() {
    Logger.log(this.name, 'refreshAll()');

    if (Viewport.isDesktop()) {
      PubSub.publish('ad:refreshAdMedRectInTab');
      PubSub.publish('ad:refreshAdSky');
      // if tandemAd was display, remove backgroundColor
      PubSub.publish('ad:removeBackgroundColor');
    } else if (Viewport.isMobile()) {
      PubSub.publish('ad:refreshAdTop');
    }
  }

  resetSitebar(): void {
    if (!this.sitebarActive) return;

    const contentWrapper = document.getElementsByClassName('wrapper')[0] as HTMLElement;
    const contentDiv = document.getElementsByClassName('content')[0] as HTMLElement;
    const rightContainer = document.getElementsByClassName('co-sky')[0] as HTMLElement;

    if (rightContainer) {
      rightContainer.removeAttribute('style');
      rightContainer.classList.remove('ad-sitebar', 'fixed');
      document.documentElement.classList.remove('has-ad-sitebar');
    }

    if (contentWrapper) contentWrapper.removeAttribute('style');
    if (contentDiv) contentDiv.removeAttribute('style');

    // reset disabled slot through exclusionRules
    this.adService.enableSlot('right');

    this.sitebarActive = false;
  }

  /** Set listeners for enabling and disabling ad-slots */
  setEnableDisableListener() {
    this.adService.on('slot.enabled', (data: IAdData) => {
      const slotId = data.slot.id;

      // handles both "tagid" and "tagid_pos" cases
      if (data.target) {
        // "tagid" case
        this.enableAd(data.target, slotId);
      } else {
        // "tagid_pos" case
        const slotIdParameter = slotId.split('_');
        const tagId = slotIdParameter[0];
        const adPosition = parseInt(slotIdParameter[1]) || -1;
        const selector = '.co-container[data-service-param-tagid="' + tagId + '"]';
        const ads = document.querySelectorAll(selector);

        if (ads.length < 1) {
          // tagId doesn't exist in DOM
          return;
        }

        if (adPosition === -1) {
          // target all ads in the document with the specific tagid
          // e.g. tagid
          for (const ad of ads) {
            if (ad instanceof HTMLElement) {
              this.enableAd(ad, tagId);
            }
          }
        } else if (adPosition <= ads.length) {
          // position is in the range of selected ads from DOM
          // e.g. tagid_1
          const ad = ads[adPosition - 1] as HTMLElement;
          this.enableAd(ad, tagId);
        }
      }
    });

    this.adService.on('slot.disabled', (data: IAdData) => {
      const target = data.target;
      const slotId = data.slot.id;
      this.disableAd(target, slotId);
    });
  }

  /** Enable an ad-slot for Target node */
  enableAd(target: HTMLElement, tagId: string) {
    if (tagId === 'top' || tagId === 'right') {
      target = target.parentNode?.parentNode as HTMLElement;
    }

    target.classList.remove('ad-disabled');

    // Rectangle container needs an additional class, to display the left column properly
    if (tagId === 'rectangle') {
      document.documentElement.classList.remove('co-rectangle-disabled');
    }

    // in case of enable of target has been triggered through tagId or tagId_1
    // then the slot that is registered in AdService has to be enabled additionally
    const slotId = target.getAttribute('data-service-slot');
    if (slotId) {
      if (!this.adService.AdSlot(slotId).config.enabled) {
        this.adService.enableSlot(slotId);
      }
    }
  }

  /** Disable an ad-slot for Target node */
  disableAd(target: HTMLElement | undefined, tagId: string) {
    if (!target?.classList) {
      return;
    }

    if (tagId === 'top' || tagId === 'right') {
      target = target.parentNode?.parentNode as HTMLElement;
    }

    if (!target) {
      return;
    }

    target.classList.add('ad-disabled');

    // Rectangle container needs an additional class, to display the left column properly
    if (tagId === 'rectangle') {
      document.documentElement.classList.add('co-rectangle-disabled');
    }
  }

  /** Some helper method? */
  disableAdSlot(tagId: string) {
    const selector = '.co-container[data-service-param-tagid="' + tagId + '"]';
    const adContainers = document.querySelectorAll(selector);

    for (const adContainer of adContainers) {
      if (adContainer instanceof HTMLElement) {
        this.disableAd(adContainer, tagId);
      }
    }
  }

  /** Track when an user clicks on a native teaser */
  enableTeaserClickoutTracking(teaserNode?: Element) {
    teaserNode?.addEventListener('click', () => {
      const clickCountURL = teaserNode.getElementsByTagName('a')[0].dataset.clickcount;
      if (clickCountURL !== '') {
        PubSub.publish('createHeaderBiddingPixel', clickCountURL);
      }
    });
  }
}

export default new Ads();
